import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Lightning, FilePdf, LinkSimple, ChatCircleDots, Warning,
} from "@phosphor-icons/react";
import InsightsWidget from "@/pages/project/InsightsWidget";
import { getBackendUrl } from "@/lib/api";

/* v3.0 · Public Client Deliverables Portal — read-only project view
   accessible via a signed URL. NO login required. */

export default function PortalPage() {
  const { pid, token } = useParams();
  const [state, setState] = useState({ loading: true, project: null, error: null });
  const [author, setAuthor] = useState("");
  const [body, setBody]     = useState("");
  const [posting, setPosting] = useState(false);

  const url = `${getBackendUrl()}/api/portal/${pid}/${token}`;

  const load = async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setState({ loading: false, project: data, error: null });
    } catch (e) {
      setState({ loading: false, project: null,
                  error: "This link is invalid or expired." });
    }
  };
  useEffect(() => { load(); }, [url]);

  const download = async () => {
    try {
      const res = await fetch(`${url}/bundle`, { method: "POST" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `deliverables_${(state.project?.name || "project").replace(/\W+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Bundle downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const postComment = async () => {
    if (!author.trim() || !body.trim()) return;
    setPosting(true);
    try {
      const res = await fetch(`${url}/comment`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ author: author.trim(), body: body.trim() }),
      });
      if (!res.ok) throw new Error();
      setBody(""); toast.success("Comment posted"); load();
    } catch {
      toast.error("Comment failed");
    } finally { setPosting(false); }
  };

  if (state.loading) return (
    <div className="min-h-screen bg-black text-gray-400 font-mono flex items-center justify-center">
      Loading portal…
    </div>
  );
  if (state.error) return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6"
         data-testid="portal-expired">
      <div className="max-w-md border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-8 text-center">
        <Warning size={40} className="mx-auto text-[#FF3B30]" />
        <div className="font-display text-2xl mt-3">Link unavailable</div>
        <p className="text-sm text-gray-400 mt-3">
          This share link is invalid or has expired. Ask your engineer to send a fresh link.
        </p>
      </div>
    </div>
  );

  const p = state.project;
  return (
    <div className="min-h-screen bg-black text-white" data-testid="portal-page">
      {/* header */}
      <div className="border-b border-white/10 bg-[#0A0C10]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lightning size={20} className="text-[#FFCC00]" weight="fill" />
            <span className="font-display font-bold">EEngDeS</span>
            <span className="text-gray-500 text-xs font-mono">· client deliverables portal</span>
          </div>
          <div className="text-[10px] font-mono text-gray-500 flex items-center gap-1">
            <LinkSimple size={12} /> read-only · signed link
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="label-tag">// project</div>
        <h1 className="font-display text-4xl font-bold mt-1">{p.name}</h1>
        <p className="text-sm text-gray-400 mt-2">
          {p.studies.length} saved {p.studies.length === 1 ? "study" : "studies"}
          {p.type ? ` · ${p.type}` : ""}
        </p>

        <div className="mt-8">
          <InsightsWidget project={p} />
        </div>

        <div className="mt-10 flex items-center flex-wrap gap-3">
          <button onClick={download}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="portal-download-bundle">
            <FilePdf size={16} weight="fill" /> Download Compliance Bundle (PDF)
          </button>
          <span className="text-[10px] font-mono text-gray-500">
            single PE-stampable PDF · every study · insights · remediation notes
          </span>
        </div>

        {/* studies list */}
        {p.studies.length > 0 && (
          <div className="mt-10">
            <div className="label-tag">// saved studies</div>
            <div className="mt-2 border border-white/10 divide-y divide-white/5">
              {p.studies.map((s) => (
                <div key={s.id} className="p-3 grid grid-cols-12 gap-3 font-mono text-xs"
                     data-testid={`portal-study-${s.id}`}>
                  <div className="col-span-5">
                    <div className="text-white text-sm font-bold">{s.name}</div>
                    <div className="text-gray-500 text-[10px] uppercase tracking-widest">{s.kind}</div>
                  </div>
                  <div className="col-span-4 text-gray-500">
                    {s.saved_at ? new Date(s.saved_at).toLocaleString() : "—"}
                  </div>
                  <div className="col-span-3 text-right">
                    {s.result
                      ? <span className="text-[#7CFF6B]">✓ has result</span>
                      : <span className="text-[#FFCC00]">no result yet</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* comments */}
        <div className="mt-10">
          <div className="label-tag">// comments</div>
          <h3 className="font-display text-xl font-bold mt-1 mb-3 flex items-center gap-2">
            <ChatCircleDots size={18} className="text-[#00E5FF]" /> Leave a note for your engineer
          </h3>
          <div className="border border-white/10 p-3 space-y-2">
            <input value={author} onChange={(e) => setAuthor(e.target.value)}
              placeholder="Your name" maxLength={80}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
              data-testid="portal-comment-name" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Approval / question / feedback…" maxLength={2000} rows={3}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
              data-testid="portal-comment-body" />
            <button onClick={postComment}
              disabled={posting || !author.trim() || !body.trim()}
              className="btn-primary text-xs disabled:opacity-40"
              data-testid="portal-comment-submit">
              {posting ? "Posting…" : "Post comment"}
            </button>
          </div>
          {(p.comments || []).length > 0 && (
            <div className="mt-4 space-y-2" data-testid="portal-comments-list">
              {p.comments.map((c) => (
                <div key={c.id}
                     className="border-l-2 border-[#00E5FF] pl-3 py-1 font-mono text-xs">
                  <div className="text-white text-sm">{c.author}</div>
                  <div className="text-gray-400 whitespace-pre-wrap">{c.body}</div>
                  <div className="text-gray-600 text-[9px]">{new Date(c.posted_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
